/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import { Helmet } from "react-helmet-async";
import React, { useEffect, useState } from "react";
import FileUploadSection from "@/src/components/Form/Common/FileUploadSection/FileUploadSection";
import LegalbirdLoader from "@/src/components/ContentLoader/LegalbirdLoader";
import ButtonLoading from "@/src/components/Button/ButtonLoading";
import requestDocumentsPageStyle from "./requestDocumentsPageStyle";
import Paper from "@mui/material/Paper";
import ApiClient from "@/src/services/apiClient";
import { useNavigate } from "react-router";
import { getCaseLink, getProductData } from "@/src/services/productService";
import { useQuery } from "@tanstack/react-query";
import { fetchResource, queryKeys } from "@/src/services/reactQuery/reactQueryService";
import useProductParams from "@/src/hooks/useProductParams";
import useMediaObjectCombinationRequest from "@/src/hooks/useMediaObjectCombinationRequest";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import LayoutProtectedRoutes from "@/src/containers/Layouts/LayoutProtectedRoutes";
import ContentBox from "@/src/components/Box/ContentBox/ContentBox";

const RequestDocumentsPage = () => {
  const { productId } = useProductParams();
  const productApiUri = getProductData("settlement", "apiUri");
  const { data: settlement, isLoading } = useQuery({
    queryKey: queryKeys.item(productApiUri, productId),
    queryFn: () => fetchResource(productApiUri, productId),
  });

  if (isLoading) {
    return (
      <LayoutProtectedRoutes>
        <LegalbirdLoader />
      </LayoutProtectedRoutes>
    );
  }
  return <RequestDocumentsPageInner settlement={settlement} />;
};

type RequestDocumentsPageInnerProps = {
  settlement: AbstractCase;
};
const RequestDocumentsPageInner = ({ settlement }: RequestDocumentsPageInnerProps) => {
  const [isBlocked, setIsBlocked] = useState(false);
  const [isLoadingInternal, setIsLoadingInternal] = useState(false);

  let mediaObjectCombinationRequestTypes = [];

  if (settlement.contract === null) {
    mediaObjectCombinationRequestTypes.push("contractFiles");
  }
  if (settlement.contractTermination === null) {
    mediaObjectCombinationRequestTypes.push("contractTerminationFiles");
  }

  const { mediaObjectCombinationRequests, isLoadingMediaObjectCombinationRequest, updateAction } = useMediaObjectCombinationRequest(
    settlement,
    mediaObjectCombinationRequestTypes,
  );

  const navigate = useNavigate();

  const triggerUpdate = async () => {
    setIsLoadingInternal(true);
    await ApiClient.post(`/generate_requested_mediaobjects/settlement/${settlement.id}`);
    navigate(getCaseLink(settlement));
  };

  useEffect(() => {
    if (settlement) {
      let hasRequestedDocument = settlement.contract === null || settlement.contractTermination === null;
      if (!hasRequestedDocument) {
        navigate(getCaseLink(settlement));
      }
    }
  }, [settlement, navigate]);

  if (isLoadingMediaObjectCombinationRequest) {
    return (
      <LayoutProtectedRoutes>
        <LegalbirdLoader />
      </LayoutProtectedRoutes>
    );
  }

  return (
    <LayoutProtectedRoutes>
      <Helmet>
        <title>Fehlende Dokumente | Legalbird</title>
      </Helmet>
      <ContentBox headline={<strong>Fehlende Dokumente ergänzen</strong>} headlineVariant={"h5"} showDivider={true}>
        Um Ihren Fall weiter bearbeiten zu können, benötigen wir noch folgende Unterlagen von Ihnen.
      </ContentBox>
      <Paper elevation={4} sx={requestDocumentsPageStyle.box}>
        {settlement.contract === null && (
          <FileUploadSection
            mediaObjectCombinationRequest={mediaObjectCombinationRequests.contractFiles}
            temporaryMediaObjectsFieldname={"contractFiles"}
            headline={"Arbeitsvertrag"}
            helperText={
              "Bitte laden Sie Ihren vollständigen Arbeitsvertrag hoch. Sie können hier mehrere Seiten hochladen. Bitte achten Sie darauf, dass alle Seiten des Vertrages vorliegen."
            }
            productEntity={settlement}
            productEntityId={settlement.id}
            product={"settlement"}
            updateAction={updateAction}
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            fieldName={"contract"}
          />
        )}
        {settlement.contractTermination === null && (
          <FileUploadSection
            mediaObjectCombinationRequest={mediaObjectCombinationRequests.contractTerminationFiles}
            temporaryMediaObjectsFieldname={"contractTerminationFiles"}
            headline={"Kündigung"}
            helperText={"Bitte laden Sie hier das Kündigungsschreiben hoch."}
            productEntity={settlement}
            productEntityId={settlement.id}
            product={"settlement"}
            updateAction={updateAction}
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            fieldName={"contractTermination"}
          />
        )}
        <br />
        <br />
        <ButtonLoading
          onClick={triggerUpdate}
          color="primary"
          variant="contained"
          disabled={false}
          fullWidth={true}
          type={"submit"}
          isLoading={isLoadingInternal}
        >
          Dokumente hochladen
        </ButtonLoading>
      </Paper>
    </LayoutProtectedRoutes>
  );
};

export default RequestDocumentsPage;
